import { cloneDeep } from 'lodash-es'
import moment from 'moment'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as api from '@/api/api'
import { StorePerformance, parsePerformance } from '@/models'
import { sortBy, upsertById } from '@/utils/utils'

import {
  PerformanceId,
  PerformanceListRead,
  PerformanceQueryParams,
  PerformanceRead,
} from '@generated/types'

// Cap the number of performances embedded in the SSR pinia hydration payload.
// Only ~2 cards fit above the fold on mobile; the rest hydrate client-side via
// loadNextMonthOfPerformances when the loader scrolls into view.
const SSR_INITIAL_LIMIT = 8

export const usePerformancePaginate = defineStore('performancePaginate', () => {
  // The show list's paginated window: today forward, booking=true, 30 days at a
  // time. Only initializePagination and loadNextMonthOfPerformances may write it.
  const performances = ref<StorePerformance[]>([])
  // Detail pages query a single show unbounded and without booking=true, so
  // their rows would otherwise become the show list's first card and poison its
  // pagination cursor.
  const detailPerformances = ref<StorePerformance[]>([])
  const isLoading = ref<boolean>(false)
  const hasMore = ref<boolean>(true)
  // Signals to SSRPreload that initializePagination() has settled (success or failure)
  // so it can serialize a populated pagination blob on `/` and `/calendar`.
  const ssrPrefetchDone = ref<boolean>(false)

  function storePerformances(performanceList: PerformanceListRead[]) {
    for (const performance of performanceList) {
      upsertById(parsePerformance(performance), performances.value)
    }
  }

  function storeDetailPerformances(performanceList: (PerformanceListRead | PerformanceRead)[]) {
    for (const performance of performanceList) {
      upsertById(parsePerformance(performance), detailPerformances.value)
    }
  }

  async function getIndividualPerformance(performanceId: PerformanceId, inviteCode?: string) {
    const query = inviteCode ? { invite_code: inviteCode } : undefined
    const response = await api.getPerformance(performanceId, query)
    storeDetailPerformances([response.data])
  }

  async function getDetailPerformances(query: PerformanceQueryParams) {
    const response = await api.getPerformances(query || {})
    storeDetailPerformances(response.data.results)
  }

  const maxPerformanceDate = computed<string | undefined>(
    () => sortBy(cloneDeep(performances.value), 'date').pop()?.date,
  )
  // `list` is raw `PerformanceListRead` (pre-`parsePerformance`), so sort by
  // `datetime` not `date` — the `date` field is added later by `parsePerformance`.
  function trimForSsr(list: PerformanceListRead[]): PerformanceListRead[] {
    const now = moment()
    const visible = list.filter(
      p => p.publish_datetime === null || moment(p.publish_datetime).isSameOrBefore(now),
    )
    return sortBy(cloneDeep(visible), 'datetime').slice(0, SSR_INITIAL_LIMIT)
  }

  async function initializePagination() {
    try {
      const endDate = moment().add(30, 'days').format('YYYY-MM-DD')

      const response = await api.getPerformances({
        booking: 'true',
        pagination: 'false',
        end_date: endDate,
      })

      // If no performances found in next 30 days, fetch all performances
      if (response.data.results.length === 0) {
        const fallback = await api.getPerformances({ booking: 'true', pagination: 'false' })
        const trimmed = trimForSsr(fallback.data.results)
        storePerformances(trimmed)
        hasMore.value = fallback.data.results.length > trimmed.length
        return
      }

      const trimmed = trimForSsr(response.data.results)
      storePerformances(trimmed)
      // A bounded 30-day window can never prove there are no shows beyond it, so
      // always let the client paginate. loadNextMonthOfPerformances is the only
      // place that can prove exhaustion: it falls back to an unbounded fetch when
      // its own +30-day query returns nothing, then flips hasMore=false.
      hasMore.value = true
    } finally {
      ssrPrefetchDone.value = true
    }
  }

  async function loadNextMonthOfPerformances() {
    if (isLoading.value) return
    isLoading.value = true

    const startDate = maxPerformanceDate.value || moment().format('YYYY-MM-DD')
    const endDate = moment(startDate).add(30, 'days').format('YYYY-MM-DD')
    const response = await api.getPerformances({
      booking: 'true',
      pagination: 'false',
      start_date: startDate,
      end_date: endDate,
    })
    const performanceLengthBeforeUpdate = performances.value.length
    storePerformances(response.data.results)
    const newPerformanceLength = performances.value.length
    if (performanceLengthBeforeUpdate === newPerformanceLength) {
      // If no new performances in next 30 days, fetch all remaining performances
      const remaining = await api.getPerformances({ booking: 'true', pagination: 'false' })
      storePerformances(remaining.data.results)
      hasMore.value = false
    } else {
      hasMore.value = true
    }
    isLoading.value = false
  }
  async function intersectionObserver(isVisible: boolean) {
    if (isVisible && hasMore.value && !isLoading.value) await loadNextMonthOfPerformances()
  }

  return {
    performances,
    detailPerformances,
    getIndividualPerformance,
    getDetailPerformances,
    initializePagination,
    intersectionObserver,
    hasMore,
    isLoading,
    ssrPrefetchDone,
  }
})
